import React, { useState } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { KeySquare, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Environment variables panel.
 * Receives the `environment_variables` array from `GET /api/admin/system-info`.
 * Sensitive values are server-side redacted; the panel only ever displays
 * what the backend chose to expose. Copy is provided for both visible and
 * redacted entries (admins often paste the prefix+length into support tickets).
 */
const EnvironmentPanel = ({ envVars = [] }) => {
  const [copiedKey, setCopiedKey] = useState(null);

  const copyValue = async (entry) => {
    if (!entry.value) return;
    try {
      await navigator.clipboard.writeText(entry.value);
      setCopiedKey(entry.key);
      toast.success(`Copied ${entry.key}`);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const setCount = envVars.filter((v) => v.set).length;
  const sensitiveCount = envVars.filter((v) => v.sensitive && v.set).length;

  return (
    <Card className="glass-card border-theme overflow-hidden" data-testid="env-panel">
      <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2 bg-rose-500/10">
        <KeySquare className="w-4 h-4 text-rose-500" />
        <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Environment Variables</h3>
        <Badge variant="outline" className="ml-2 text-[10px]">{setCount} of {envVars.length} set</Badge>
        <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500">
          {sensitiveCount} sensitive
        </Badge>
        <div className="ml-auto text-[10px] text-muted-foreground hidden md:block">
          Sensitive values are server-side redacted — only prefix &amp; length are shown.
        </div>
      </div>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr className="text-left text-muted-foreground">
                <th className="px-4 py-2 font-medium">Variable</th>
                <th className="px-4 py-2 font-medium">Value</th>
                <th className="px-4 py-2 font-medium text-center">Status</th>
                <th className="px-4 py-2 font-medium text-right" style={{ width: 70 }}>Copy</th>
              </tr>
            </thead>
            <tbody>
              {envVars.map((v, i) => (
                <tr key={v.key} className="border-t border-border/40 hover:bg-muted/20" data-testid={`env-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-foreground">{v.key}</td>
                  <td className="px-4 py-2 font-mono text-muted-foreground break-all">
                    {v.set
                      ? <span>{v.value}</span>
                      : <span className="text-muted-foreground/50 italic">not set</span>}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {v.set ? (
                      v.sensitive ? (
                        <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500">redacted</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-500">visible</Badge>
                      )
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-zinc-500/30 text-zinc-400">empty</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {v.set && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => copyValue(v)}
                        title="Copy value"
                        data-testid={`env-copy-${v.key}`}
                      >
                        {copiedKey === v.key ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {envVars.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No environment data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default EnvironmentPanel;
